import { FC, useMemo } from 'react';
import { AxiosError } from 'axios';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { CategoryTypeEnum } from 'entities/Files/types';
import { postDirectImport } from 'entities/ImportSessions/api';
import { ActionsRow } from 'widgets/importFiles/importFilesDirect/ui/ActionsRow';
import { ImportFilesDashboard } from 'widgets/importFiles/common/ui/ImportFilesDashboard';
import { PageHeader } from 'shared/ui/PageHeader';
import { useImportFilesStore } from 'widgets/importFiles/state';
import { getImportSessions } from 'entities/ImportSessions/api';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { DnDImportFiles } from 'shared/ui/DndImportFiles';
import { PageWrapper } from 'pages/_layouts/PageWrapper';

export const ImportFilesDirect: FC = () => {
	const { t } = useTranslation();
	const { data, setData } = useImportFilesStore();
	const [searchParams] = useSearchParams();
	const categoryType = searchParams.get('category');
	const generativeId = searchParams.get('generative-model-id');
	const customModelId = searchParams.get('custom-model-id');

	const { refetch } = useQuery({
		queryKey: ['get/import-sessions'],
		queryFn: () => getImportSessions(),
		onSuccess: (response) => {
			setData(response.data);
			const isUploading = response.data
				.map(
					(item) =>
						item.importSessionFiles.filter((sessionItem) => sessionItem.status === 'UPLOADING')
							.length,
				)
				.filter((item) => item).length;

			if (isUploading) {
				setTimeout(() => {
					refetch();
				}, 6000);
			}
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const { mutate: directMutate, isLoading: directLoading } = useMutation({
		mutationKey: ['direct-import-files'],
		mutationFn: postDirectImport,
		onSuccess: () => refetch(),
		onError: (err) => {
			showErrorNotification(err as AxiosError, t('files.snackbar.error_while_uploading'));
		},
	});

	const importSession = useMemo(() => {
		return data?.find((session) => session.uploadSource === 'DIRECT');
	}, [data]);

	const files = useMemo(() => {
		return importSession ? importSession.importSessionFiles : [];
	}, [importSession]);

	const uploadedFilesHandler = (files: Array<File>) => {
		const formData = new FormData();
		formData.append('category', categoryType as CategoryTypeEnum);
		if (customModelId) {
			formData.append('customModelId', customModelId || '');
		}
		if (generativeId) {
			formData.append('generativeModelId', generativeId || '');
		}
		Object.values(files).forEach((file) => {
			formData.append('files', file);
		});
		directMutate(formData);
	};

	if (!categoryType) {
		return <Navigate to={'/'} />;
	}

	return (
		<PageWrapper>
			<PageHeader
				title={t('start_recognition.upload_files_section.title')}
				description={t('start_recognition.upload_files_section.description')}
			/>
			<DnDImportFiles
				setUploadedFiles={uploadedFilesHandler}
				fileDescription={t('start_recognition.how_to_start.step_2.li_1')}
				importingProccess={{
					loading: directLoading,
					importedFiles: 0,
				}}
			/>
			<ImportFilesDashboard categoryType={importSession?.category} files={files} />
			{importSession && <ActionsRow importSession={importSession} />}
		</PageWrapper>
	);
};
